import React, { useState } from 'react';
import { Gift, Crown, Users, Calendar, Sunrise, Heart, Sparkles, Mail, X, CheckCircle, AlertCircle } from 'lucide-react';
import { mockData } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import './FreeGyyfts.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  'gift': Gift,
  'crown': Crown,
  'users': Users,
  'calendar': Calendar,
  'sunrise': Sunrise,
  'heart': Heart
};

const FreeGyyfts = () => {
  const [selectedGyyft, setSelectedGyyft] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleClaimClick = (gyyfts) => {
    setSelectedGyyft(gyyfts);
    setSubmitStatus(null);
  };

  const handleCloseModal = () => {
    setSelectedGyyft(null);
    setFormData({ name: '', email: '' });
    setSubmitStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/claim-gyyft`, {
        name: formData.name,
        email: formData.email,
        gyyftsName: selectedGyyft.name,
        gyyftsCode: selectedGyyft.code,
        gyyftsDescription: selectedGyyft.description
      });

      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '' });
        
        setTimeout(() => {
          handleCloseModal();
        }, 3000);
      }
    } catch (error) {
      console.error('Error claiming Gyyft:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-yellow-100">
      {/* Header */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="text-yellow-400" size={40} />
            <h1 className="text-5xl md:text-6xl font-bold">
              Free <span className="text-yellow-400">Gyyfts</span>
            </h1>
            <Sparkles className="text-yellow-400" size={40} />
          </div>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Claim your exclusive bHyyve rewards! Click any gift to claim yours instantly.
          </p>
        </div>
      </div>

      {/* Gyyfts Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {mockData.freeGyyfts.map((gyyfts) => {
            const Icon = iconMap[gyyfts.icon];
            return (
              <div
                key={gyyfts.id}
                className="gyyft-card group relative bg-white rounded-3xl p-8 border-4 border-black/10 hover:border-yellow-400 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 cursor-pointer"
                onClick={() => handleClaimClick(gyyfts)}
              >
                {/* Hover Bee Animation */}
                <div className="hover-bee">
                  <div className="hover-bee-body">
                    <div className="hover-bee-stripe"></div>
                    <div className="hover-bee-stripe"></div>
                  </div>
                  <div className="hover-bee-wings">
                    <div className="hover-bee-wing left"></div>
                    <div className="hover-bee-wing right"></div>
                  </div>
                </div>

                {/* Icon Circle */}
                <div className={`${gyyfts.color} w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <Icon className="text-white" size={48} />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-black text-center mb-3">
                  {gyyfts.name}
                </h3>
                <p className="text-gray-600 text-center mb-4">
                  {gyyfts.description}
                </p>

                {/* Code Badge */}
                <div className="bg-gray-100 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-500 text-center mb-1">Promo Code</p>
                  <p className="text-sm font-mono font-bold text-black text-center">
                    {gyyfts.code}
                  </p>
                </div>

                {/* Claim Button */}
                <button className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 group-hover:shadow-lg">
                  <Mail size={20} />
                  <span>Claim Now!</span>
                </button>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-yellow-400/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              </div>
            );
          })}
        </div>

        {/* Instructions */}
        <div className="max-w-3xl mx-auto mt-16 bg-white border-4 border-yellow-400 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-black mb-4">How to Claim Your Gyyft</h2>
          <ol className="text-left space-y-3 text-gray-700">
            <li className="flex items-start gap-3">
              <span className="bg-yellow-400 text-black font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">1</span>
              <span>Click on the gift you want to claim</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-yellow-400 text-black font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">2</span>
              <span>Fill in your name and email in the claim form</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-yellow-400 text-black font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">3</span>
              <span>Submit your claim and we'll email you the details</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-yellow-400 text-black font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">4</span>
              <span>Use your code within 30 days to redeem your reward!</span>
            </li>
          </ol>
        </div>
      </div>

      {/* Claim Modal */}
      {selectedGyyft && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 relative shadow-2xl">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
            >
              <X size={24} />
            </button>

            {/* Modal Header */}
            <div className={`${selectedGyyft.color} w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4`}>
              {React.createElement(iconMap[selectedGyyft.icon], { className: "text-white", size: 40 })}
            </div>
            <h2 className="text-2xl font-bold text-black text-center mb-2">
              {selectedGyyft.name}
            </h2>
            <p className="text-gray-600 text-center mb-6">{selectedGyyft.description}</p>

            {/* Success Message */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-400 rounded-lg p-4 flex items-start gap-3 mb-6">
                <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                <div>
                  <p className="text-green-800 font-semibold">Claim Successful!</p>
                  <p className="text-green-700 text-sm">Check your email for your Gyyft code.</p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 flex items-start gap-3 mb-6">
                <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
                <div>
                  <p className="text-red-800 font-semibold">Claim Failed</p>
                  <p className="text-red-700 text-sm">Please try again or contact support@bhyyve.com</p>
                </div>
              </div>
            )}

            {/* Claim Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-black mb-2">
                  Your Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting || submitStatus === 'success'}
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-black mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting || submitStatus === 'success'}
                  className="w-full"
                />
              </div>

              <div className="bg-gray-100 rounded-lg p-3">
                <p className="text-xs text-gray-500 text-center mb-1">Your Code</p>
                <p className="text-sm font-mono font-bold text-black text-center">
                  {selectedGyyft.code}
                </p>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || submitStatus === 'success'}
                className="w-full bg-yellow-400 text-black hover:bg-yellow-500 font-bold py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black mr-2"></div>
                    Claiming...
                  </>
                ) : submitStatus === 'success' ? (
                  'Claimed Successfully!'
                ) : (
                  'Claim My Gyyft'
                )}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Saving?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Download the bHyyve app and redeem your Gyyfts today!
          </p>
          <a
            href="/"
            className="inline-block bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 px-8 rounded-xl transition-all duration-200"
          >
            Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default FreeGyyfts;
